import { DateTime } from "@dmca/utils/luxon";
import type { IGHGResource } from "@dmca/models/interface/ghg";
import {
  PulseMeterResourceTypeEnum,
  type IPulseMeter,
} from "@dmca/models/interface/pulse-meter";

/** Change-only reporting has no age timeout; connectivity comes from heartbeat. */
export function availableMachineObservation(
  measurement: { value: number; createdAt: DateTime } | null | undefined,
  connectivity: "online" | "offline" | undefined,
  failed: boolean,
  now: DateTime = DateTime.now()
) {
  return !failed &&
    connectivity === "online" &&
    measurement &&
    Number.isFinite(measurement.value) &&
    measurement.createdAt.isValid &&
    measurement.createdAt.toMillis() <= now.toMillis()
    ? measurement
    : null;
}

const secondsPerUnit: Record<string, number> = {
  s: 1,
  sec: 1,
  second: 1,
  seconds: 1,
  m: 60,
  min: 60,
  minute: 60,
  minutes: 60,
  h: 3600,
  hr: 3600,
  hour: 3600,
  hours: 3600,
  d: 86400,
  day: 86400,
  days: 86400,
};
const unitToBase: Record<string, { type: "volume" | "energy"; value: number }> =
  {
    "m³": { type: "volume", value: 1 },
    "m^3": { type: "volume", value: 1 },
    m3: { type: "volume", value: 1 },
    "ft³": { type: "volume", value: 0.0283168 },
    "ft^3": { type: "volume", value: 0.0283168 },
    gal: { type: "volume", value: 0.00378541 },
    L: { type: "volume", value: 0.001 },
    kWh: { type: "energy", value: 1 },
    Wh: { type: "energy", value: 0.001 },
    W: { type: "energy", value: 0.001 },
  };

export function projectedMonthlyUsage(
  meters: IPulseMeter[],
  usage: Record<string, { immediateValue: number | null }>,
  resource: IGHGResource,
  secondsInMonth = (DateTime.now().daysInMonth ?? 0) * 86400
) {
  const resourceType =
    resource.id === "electrical"
      ? PulseMeterResourceTypeEnum.ELECTRICITY
      : resource.id === "water"
      ? PulseMeterResourceTypeEnum.WATER
      : resource.id === "gas"
      ? PulseMeterResourceTypeEnum.GAS
      : undefined;
  if (!resourceType) return undefined;
  const matching = meters.filter(
    (meter) => meter.resourceType === resourceType
  );
  if (!matching.length) return undefined;
  let total = 0;
  for (const meter of matching) {
    const rate = usage[meter.id]?.immediateValue;
    const seconds = secondsPerUnit[meter.timeMeasurementType.toLowerCase()];
    if (
      rate == null ||
      !Number.isFinite(rate) ||
      rate < 0 ||
      seconds === undefined
    )
      return undefined;
    let value = (rate * secondsInMonth) / seconds;
    if (meter.valueMeasurementType !== resource.unitOfMeasurement) {
      const from = unitToBase[meter.valueMeasurementType];
      const to = unitToBase[resource.unitOfMeasurement];
      if (!from || !to || from.type !== to.type) return undefined;
      value = (value * from.value) / to.value;
    }
    if (!Number.isFinite(value)) return undefined;
    total += value;
  }
  return Number.isFinite(total) ? total : undefined;
}

/** Include wholly absent months as gaps, rather than joining across them. */
export function ghgUsageDataset(
  partition: Record<string, Record<string, number>>,
  resourceId: string,
  start: DateTime,
  end: DateTime
) {
  const rows: { time: Date; value: number | null }[] = [];
  for (
    let month = start.toUTC().startOf("month");
    month <= end;
    month = month.plus({ months: 1 })
  ) {
    const value = partition[month.toSeconds()]?.[resourceId];
    rows.push({
      time: month.toJSDate(),
      value: typeof value === "number" && Number.isFinite(value) ? value : null,
    });
  }
  return rows.some((row) => row.value !== null) ? rows : [];
}
